import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Check,
  ArrowLeft,
  Zap,
  ShoppingCart,
  BarChart3,
  Sparkles,
  Shield,
  CreditCard,
  TrendingUp,
  Store,
  Percent
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const FEATURES = [
  { name: "Product Dashboard", desc: "List and manage unlimited products", icon: Store },
  { name: "Online Storefront", desc: "Your own branded store page", icon: ShoppingCart },
  { name: "Stripe Payments", desc: "Accept cards, Apple Pay, Google Pay", icon: CreditCard },
  { name: "CRM Solutions", desc: "Manage customer relationships", icon: TrendingUp },
  { name: "AI Content Studio", desc: "Generate images, videos & social posts", icon: Sparkles },
  { name: "POS System", desc: "In-store point of sale", icon: Zap },
  { name: "Analytics Dashboard", desc: "Track sales, orders & performance", icon: BarChart3 },
  { name: "Secure & Compliant", desc: "SSL, GDPR compliant, data protection", icon: Shield },
];

const MerchantPricingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950">
      {/* Header */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY_LOGO} alt="V" className="h-8 w-auto brightness-0 invert" />
            <div className="h-6 w-px bg-white/40" />
            <span className="text-white font-brand text-lg font-bold">VEYRONIX</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-white/10">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-medium mb-6">
            <Percent className="h-4 w-4" />
            Simple, transparent pricing
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            No Monthly Fees. Ever.
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            We only make money when you make money. Pay a simple 5% commission on each successful transaction. No setup fees, no hidden costs.
          </p>
        </div>

        {/* Commission Card */}
        <div className="max-w-xl mx-auto mb-16">
          <div className="relative rounded-2xl overflow-hidden" data-testid="commission-card">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700" />
            <div className="relative p-10 text-center">
              <p className="text-indigo-200 text-sm font-medium uppercase tracking-wider mb-2">Commission per transaction</p>
              <div className="flex items-baseline justify-center gap-1 mb-4">
                <span className="text-7xl font-bold text-white">5</span>
                <span className="text-3xl font-bold text-white">%</span>
              </div>
              <p className="text-indigo-100 mb-8 max-w-sm mx-auto">
                That's it. You keep 95% of every sale. No monthly subscriptions, no setup fees, no minimum commitments.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-indigo-700 hover:bg-indigo-50 px-10 py-6 text-lg font-semibold rounded-xl">
                  Start Selling for Free
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: "1", title: "Sign Up Free", desc: "Create your merchant account in minutes. No credit card required." },
              { step: "2", title: "List Products", desc: "Add your products or services. Set prices, descriptions, and images." },
              { step: "3", title: "Get Paid", desc: "When customers buy, you receive 95% directly to your Stripe account." },
            ].map(({ step, title, desc }) => (
              <div key={step} className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <div className="w-10 h-10 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {step}
                </div>
                <h3 className="text-white font-semibold mb-2">{title}</h3>
                <p className="text-gray-400 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* All Features */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-2">Everything Included</h2>
          <p className="text-gray-400 text-center mb-8">All features are available to every merchant. No tiers, no upgrades.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map(({ name, desc, icon: Icon }) => (
              <div key={name} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
                <Icon className="h-6 w-6 text-indigo-400 mb-3" />
                <h3 className="text-white font-medium text-sm mb-1">{name}</h3>
                <p className="text-gray-500 text-xs">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Example calculation */}
        <div className="max-w-2xl mx-auto mb-16 bg-white/5 border border-white/10 rounded-2xl p-8">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Example Calculation</h3>
          <div className="space-y-4">
            {[
              { sale: "£100", commission: "£5", youKeep: "£95" },
              { sale: "£500", commission: "£25", youKeep: "£475" },
              { sale: "£1,000", commission: "£50", youKeep: "£950" },
            ].map(({ sale, commission, youKeep }) => (
              <div key={sale} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                <span className="text-gray-400">Sale: <span className="text-white font-medium">{sale}</span></span>
                <span className="text-gray-400">Commission: <span className="text-orange-400 font-medium">{commission}</span></span>
                <span className="text-gray-400">You keep: <span className="text-emerald-400 font-bold">{youKeep}</span></span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Start Selling?</h2>
          <p className="text-gray-400 mb-6">Join thousands of merchants already selling on <span className="font-brand">VEYRONIX</span></p>
          <Link to="/register">
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-6 text-lg rounded-xl">
              Create Free Account
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MerchantPricingPage;
